import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { SparklesIcon } from '@heroicons/react/24/outline';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, token, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Handle authentication redirect
  useEffect(() => {
    if (!loading && !token) {
      const currentPath = location.pathname + location.search;
      navigate('/login', { 
        replace: true, 
        state: { returnUrl: currentPath } 
      });
    }
  }, [token, loading, navigate, location.pathname, location.search]);

  // Show loading screen while auth is being checked
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-indigo-50 to-purple-50">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto"></div>
            <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-r-purple-600 rounded-full animate-pulse mx-auto"></div>
            <SparklesIcon className="absolute inset-0 w-8 h-8 text-indigo-600 m-auto" />
          </div>
          <p className="mt-6 text-lg text-gray-600 font-medium">Loading LeanAI...</p>
          <p className="mt-2 text-sm text-gray-400">Authenticating your session</p>
        </div>
      </div>
    );
  }

  // If no token, return null while redirect happens
  if (!token || !user) {
    return null;
  }

  return <>{children}</>;
};

export default ProtectedRoute;